import React, { useMemo } from 'react';
import { Alert, Col, Row, Spinner } from 'react-bootstrap';
import { getSalesValue, useSalesApiList } from '../hooks/useSalesApiResource';
import './CSMTableList.css';

const keyTypeClassMap = {
  1: 'availabletab',
  2: 'standardtab',
  3: 'windloadtab',
  4: 'oversizetab',
  5: 'noavailabletab',
};

const getCellClassName = (keyType) => {
  if (typeof keyType !== 'number') return 'noavailabletab';
  return keyTypeClassMap[keyType] ?? 'noavailabletab';
};

const toInches = (feet, inches) => Number(inches || 0) || Math.round(Number(feet || 0) * 12);

const normalizeEnclosureRow = (row = {}) => {
  const widthFeet = Number(getSalesValue(row, 'widthInFeet', 'WidthInFeet') || 0);
  const widthInches = toInches(widthFeet, getSalesValue(row, 'widthInInches', 'WidthInInches'));
  const lengthFeet = Number(getSalesValue(row, 'lengthInFeet', 'LengthInFeet') || 0);
  const lengthInches = toInches(lengthFeet, getSalesValue(row, 'lengthInInches', 'LengthInInches'));

  return {
    id: Number(getSalesValue(row, 'id', 'Id') || 0),
    width: { feet: widthFeet || widthInches / 12, inches: widthInches },
    length: { feet: lengthFeet || lengthInches / 12, inches: lengthInches },
    type: String(getSalesValue(row, 'enclosureType', 'EnclosureType') || '1'),
    amount: getSalesValue(row, 'finalPrice', 'FinalPrice') == null ? null : Number(getSalesValue(row, 'finalPrice', 'FinalPrice')),
    keyType: Number(getSalesValue(row, 'keyType', 'KeyType') || 5),
  };
};

const normalizeEnclosureRows = (rows) => rows.map(normalizeEnclosureRow);

const buildTableModel = (rows) => {
  const lengthColumns = Array.from(
    new Map(rows.map((item) => [`${item.length.inches}_${item.length.feet}`, { inches: item.length.inches, feet: item.length.feet }])).values()
  ).sort((first, second) => toInches(first.feet, first.inches) - toInches(second.feet, second.inches));

  const widthRows = Array.from(
    new Map(rows.map((item) => [`${item.width.inches}_${item.width.feet}`, { inches: item.width.inches, feet: item.width.feet }])).values()
  ).sort((first, second) => toInches(first.feet, first.inches) - toInches(second.feet, second.inches));

  return {
    lengthColumns,
    rows: widthRows.map((width) => ({
      widthInches: width.inches,
      widthFeet: width.feet,
      cells: lengthColumns.map((length) =>
        rows.find((row) =>
          row.width.inches === width.inches &&
          row.length.inches === length.inches
        ) ?? null
      ),
    })),
  };
};

const formatAmount = (row) => {
  if (!row || row.amount === null) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(row.amount);
};

const filterDisplayRows = (rows) => rows.filter((row) => row.width.inches >= 72 && row.length.inches >= 72);

const CsmPriceTable = ({ model, prefix }) => (
  <div className="table-headers">
    <div className="width-header">Width</div>
    <div style={{ width: '100%' }}>
      <h6 className="length-header">Length</h6>
      <table className="table">
        <thead>
          <tr>
            <th className="inchesRowHeader">Inches</th>
            <th className="inchesRowHeader"></th>
            {model.lengthColumns.map((column) => (
              <th key={`${prefix}-inch-${column.inches}-${column.feet}`} className="inchesRowHeader">{column.inches}</th>
            ))}
          </tr>
          <tr>
            <th className="feetRowHeader"></th>
            <th className="feetRowHeader">Feet</th>
            {model.lengthColumns.map((column) => (
              <th key={`${prefix}-feet-${column.inches}-${column.feet}`} className="feetRowHeader">{column.feet}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {model.rows.map((row) => (
            <tr key={`${prefix}-row-${row.widthInches}-${row.widthFeet}`}>
              <td className="inchesRowHeader">{row.widthInches}</td>
              <td className="feetRowHeader">{row.widthFeet}</td>
              {row.cells.map((cell, index) => (
                <td key={`${prefix}-cell-${row.widthInches}-${row.widthFeet}-${index}`} className={getCellClassName(cell?.keyType)}>
                  {formatAmount(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const CSMTableList = () => {
  const { items: enclosureRows, loading, error } = useSalesApiList((endpoints) => endpoints.enclosurePricings.base, {
    normalize: normalizeEnclosureRows,
    errorMessage: 'Unable to load CSM enclosure pricing.',
  });

  const standardRows = useMemo(() => filterDisplayRows(enclosureRows.filter((item) => item.type === '1')), [enclosureRows]);
  const ratedRows = useMemo(() => filterDisplayRows(enclosureRows.filter((item) => item.type === '2')), [enclosureRows]);
  const standardModel = useMemo(() => buildTableModel(standardRows), [standardRows]);
  const ratedModel = useMemo(() => buildTableModel(ratedRows), [ratedRows]);
  const windLoadAdder = useMemo(
    () => enclosureRows.find((row) => row.type === 'string' || row.id === 230)?.amount ?? 5184,
    [enclosureRows]
  );

  return (
    <section className="sales-portal-page" id="csmtable" aria-labelledby="sales-csm-title">
      <Row className="mb-3">
        <Col><h4 id="sales-csm-title">CSM Table List</h4></Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="headsec d-flex flex-wrap mb-3">
        <div className="availabletab key-padding flex-fill text-center">Available</div>
        <div className="standardtab key-padding flex-fill text-center">Standard Size</div>
        <div className="windloadtab key-padding flex-fill text-center">Wind Load</div>
        <div className="oversizetab key-padding flex-fill text-center">Oversize Load</div>
        <div className="noavailabletab key-padding flex-fill text-center">Not Available</div>
      </div>

      <h6>CONESTOGA TRAILER SHIPPING NOTES:</h6>
      <ol className="desc-fsize">
        <li>Standard Conestoga trailers can accept equipment 100&quot; wide by 100&quot; high.</li>
        <li>Stations without a CSM enclosure, shipping via Conestoga, maximum base width should be 100&quot;.</li>
        <li>Stations with a CSM enclosure, shipping via Conestoga, maximum base width should be 96&quot;.</li>
        <li>Stations with a CSM enclosure, the standard enclosure height is 102&quot;.</li>
        <li>A step deck Conestoga trailer is required for any station with a CSM enclosure.</li>
        <li>Conestoga trailers cost $500-$1000 more than a standard flatbed.</li>
      </ol>

      {loading ? (
        <div className="sales-no-data"><Spinner animation="border" size="sm" /> Loading CSM pricing...</div>
      ) : (
        <>
          <h4 className="mt-4">STANDARD ENCLOSURE PRICING</h4>
          <CsmPriceTable model={standardModel} prefix="standard" />

          <h4>
            WIND LOAD RATED ENCLOSURE PRICING <span style={{ textTransform: 'none' }}>(Includes ${windLoadAdder} price adder)</span>
          </h4>
          <CsmPriceTable model={ratedModel} prefix="rated" />
        </>
      )}

      <h6>Notes:</h6>
      <ol className="desc-fsize">
        <li>Dimensions reflected above include approximate outside dimensions of enclosure and base/skid. Enclosure will have handles and other hardware items that may protrude outside these dimensions.</li>
        <li>Composite Enclosures are part of our &quot;PACKAGED PUMPING SYSTEM&quot; UL RATING and can be used with any job that requires UL approval.</li>
        <li>A composite enclosure sits on top of base/skid and requires a minimum 6&quot; wide open space around the top perimeter surface of base to facilitate the enclosure wall structure.</li>
        <li>All composite enclosure layouts should be reviewed by CSM to ensure design standards are met prior to submittal to customer.</li>
      </ol>

      <h6>OPTIONS:</h6>
      <ul className="desc-fsize">
        <li className="desc-list-subheading">CUSTOM SIZES:</li>
        <li>Use of standard base and enclosure sizes from table above is strongly recommended when ever possible.</li>
        <li>In the event a custom size is required, approval of layout drawing SHALL be approved by CSM PRIOR to submittal to customer.</li>
        <li>Enter a CUSTOM OPTION in price matrix for custom size composite enclosures. Description of option should be <strong>Composite Enclosure- Custom Size</strong></li>
        <li>LIST PRICE will be provided by CSM upon approval of layout drawing.</li>
      </ul>
      <ul className="desc-fsize">
        <li className="desc-list-subheading">CUSTOM COLORS:</li>
        <li>Sandstone and Green are standard colors and included in standard composite enclosure pricing.</li>
        <li>ALL other colors are CUSTOM and must be matched to a customer provided paint chip.</li>
        <li>Please add 3-4 weeks extra lead time for custom color jobs.</li>
        <li>Select Option Number 911-0000005 in Category 911 - Composite Enclosure Accessories to add this feature to your quote.</li>
        <li>LIST PRICE for all custom color options is <span style={{ color: 'blue' }}>$2633</span>. (not reflected in pricing above)</li>
      </ul>
      <ul className="desc-fsize">
        <li className="desc-list-subheading">SOUND ATTENUATION INSULATION PACKAGE:</li>
        <li>Select Option Number 911-0000003 in Category 911 - Composite Enclosure Accessories to add the standard feature to your quote, or select Option Number 911-0000004 to add this feature for a base longer than 15 ft.</li>
        <li>LIST PRICE: <span style={{ color: 'blue' }}>$2700</span> for enclosures up to 15ft in length. <span style={{ color: 'blue' }}>$3240</span> for enclosures over 15&apos; in length. (not reflected in pricing above)</li>
      </ul>
      <ul className="desc-fsize">
        <li className="desc-list-subheading">WIND LOAD RATING OPTION:</li>
        <li>Wind load rated enclosure systems are rated for wind speeds up to 160 mph.</li>
        <li>Wind load rated enclosures incorporate additional structure and design modifications that are NOT included in standard enclosures.</li>
        <li>Drawings will be PE stamped.</li>
        <li>Wind load rated systems have design limitations and must be reviewed by CSM to ensure compliance PRIOR to submittal to customer.</li>
        <li>Enclosure sizes are limited to sizes in table above.</li>
        <li>Select Option Number 911-0000002 in Category 911 - Composite Enclosure Accessories to add this feature to your quote.</li>
        <li>LIST PRICE: <span style={{ color: 'blue' }}>$5184</span></li>
      </ul>
    </section>
  );
};

export default CSMTableList;
